import { JSX, useState, useContext, useMemo, useEffect, useCallback, useRef } from 'react';
import './sidebar.css';
import testIdFormatter from '@/utils/testIdFormatter';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { NWLContext } from '../../../contexts';

import HDLogo from '/img/hd_logo.png';
import HDLogoCollapsed from '/img/HD_logo_collapsed.png';
import NWLLogo from '/img/NWL_logo.png';
import NWLLogoCollapsed from '/img/NWL_logo_collapsed.png';
import ModalExit from '@/components/ModalExit';
import { CountdownInfoModal } from '@/components/Modal/CountdownInfo';
import {
	getDigitalTwins,
	getDigitalTwinsNextSimulation,
	getDigitalTwinsSimulations,
} from '../../../services/digitalTwins';
import { lockFirstSuccessCompletedSimTime } from '../../utils/simSuccessTime';
import { useActiveLocationId, setActiveLocationId } from '../../utils/activeLocation';

interface Menu {
	icon: JSX.Element;
	label: string;
	href: string;
}

const menu: Menu[] = [
	{
		icon: (
			<svg
				id="dashboard-icon"
				viewBox="0 0 24 24"
				fill="currentColor"
				xmlns="http://www.w3.org/2000/svg"
				className="size-8"
			>
				<g clipPath="url(#clip0_1556_205129)">
					<path d="M23.6384 11.8125L13.2884 1.46784L12.5946 0.774087C12.4366 0.61706 12.2228 0.528931 12 0.528931C11.7772 0.528931 11.5634 0.61706 11.4053 0.774087L0.361593 11.8125C0.199622 11.9738 0.0716145 12.166 -0.0148749 12.3776C-0.101364 12.5892 -0.144583 12.816 -0.141979 13.0446C-0.131265 13.9875 0.653557 14.7402 1.59641 14.7402H2.73481V23.4643H21.2652V14.7402H22.4277C22.8857 14.7402 23.3169 14.5607 23.6411 14.2366C23.8006 14.0775 23.9271 13.8884 24.0131 13.6801C24.0991 13.4718 24.1429 13.2485 24.1419 13.0232C24.1419 12.5678 23.9625 12.1366 23.6384 11.8125ZM13.5 21.5357H10.5V16.0714H13.5V21.5357ZM19.3366 12.8116V21.5357H15.2143V15.4286C15.2143 14.8366 14.7348 14.3571 14.1428 14.3571H9.85713C9.26516 14.3571 8.7857 14.8366 8.7857 15.4286V21.5357H4.66338V12.8116H2.09195L12.0027 2.90891L12.6214 3.52766L21.9107 12.8116H19.3366Z" />
				</g>
				<defs>
					<clipPath id="clip0_1556_205129">
						<rect width="24" height="24" fill="white" />
					</clipPath>
				</defs>
			</svg>
		),
		label: 'Dashboard',
		href: '/nwl',
	},
	{
		icon: (
			<svg
				id="decision-log-icon"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className="size-8"
			>
				<path
					d="M16.7143 6.64284V5.35713C16.7143 5.23927 16.6179 5.14284 16.5 5.14284H6.21428C6.09643 5.14284 6 5.23927 6 5.35713V6.64284C6 6.7607 6.09643 6.85713 6.21428 6.85713H16.5C16.6179 6.85713 16.7143 6.7607 16.7143 6.64284ZM6.21428 8.99998C6.09643 8.99998 6 9.09641 6 9.21427V10.5C6 10.6178 6.09643 10.7143 6.21428 10.7143H11.1429C11.2607 10.7143 11.3571 10.6178 11.3571 10.5V9.21427C11.3571 9.09641 11.2607 8.99998 11.1429 8.99998H6.21428ZM16.2857 12.1071C13.0902 12.1071 10.5 14.6973 10.5 17.8928C10.5 21.0884 13.0902 23.6786 16.2857 23.6786C19.4812 23.6786 22.0714 21.0884 22.0714 17.8928C22.0714 14.6973 19.4812 12.1071 16.2857 12.1071ZM19.1652 20.7723C18.3964 21.5411 17.3732 21.9643 16.2857 21.9643C15.1982 21.9643 14.175 21.5411 13.4062 20.7723C12.6375 20.0036 12.2143 18.9803 12.2143 17.8928C12.2143 16.8053 12.6375 15.7821 13.4062 15.0134C14.175 14.2446 15.1982 13.8214 16.2857 13.8214C17.3732 13.8214 18.3964 14.2446 19.1652 15.0134C19.9339 15.7821 20.3571 16.8053 20.3571 17.8928C20.3571 18.9803 19.9339 20.0036 19.1652 20.7723ZM18.6696 15.8571H17.483C17.4134 15.8571 17.3491 15.8893 17.3089 15.9455L15.608 18.2973L14.9893 17.4428C14.9695 17.4152 14.9433 17.3928 14.913 17.3774C14.8827 17.362 14.8492 17.3542 14.8152 17.3544H13.6339C13.4598 17.3544 13.358 17.5527 13.4598 17.6946L15.4366 20.4294C15.5223 20.5473 15.6964 20.5473 15.7821 20.4294L18.8411 16.1973C18.9455 16.0553 18.8437 15.8571 18.6696 15.8571ZM10.0714 21.1071H3.85714V2.24998H18.8571V11.4643C18.8571 11.5821 18.9536 11.6786 19.0714 11.6786H20.5714C20.6893 11.6786 20.7857 11.5821 20.7857 11.4643V1.17855C20.7857 0.704447 20.4027 0.321411 19.9286 0.321411H2.78571C2.31161 0.321411 1.92857 0.704447 1.92857 1.17855V22.1786C1.92857 22.6527 2.31161 23.0357 2.78571 23.0357H10.0714C10.1893 23.0357 10.2857 22.9393 10.2857 22.8214V21.3214C10.2857 21.2036 10.1893 21.1071 10.0714 21.1071Z"
					fill="currentColor"
				/>
			</svg>
		),
		label: 'Decision Log',
		href: '/nwl/decision-log',
	},
];

const settingsMenu: Menu[] = [
	{
		icon: (
			<svg
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className="size-8"
				id="settings-icon"
			>
				<path
					d="M19.9 12.6599C19.7397 12.4774 19.6513 12.2428 19.6513 11.9999C19.6513 11.757 19.7397 11.5224 19.9 11.3399L21.18 9.89989C21.321 9.74256 21.4086 9.5446 21.4302 9.3344C21.4517 9.12421 21.4062 8.91258 21.3 8.72989L19.3 5.2699C19.1949 5.08742 19.0349 4.94277 18.8427 4.85658C18.6506 4.77039 18.4362 4.74705 18.23 4.7899L16.35 5.1699C16.1108 5.21932 15.8617 5.17948 15.6499 5.0579C15.438 4.93631 15.278 4.74138 15.2 4.5099L14.59 2.6799C14.5229 2.48127 14.3951 2.30876 14.2246 2.18674C14.0542 2.06471 13.8496 1.99935 13.64 1.9999H9.64C9.42193 1.98851 9.20613 2.04882 9.02556 2.17161C8.84499 2.2944 8.70958 2.47291 8.64 2.6799L8.08 4.5099C8.002 4.74138 7.84197 4.93631 7.63011 5.0579C7.41825 5.17948 7.16922 5.21932 6.93 5.1699L5 4.7899C4.80455 4.76228 4.6053 4.79312 4.42735 4.87853C4.24939 4.96395 4.1007 5.10012 4 5.2699L2 8.72989C1.89116 8.91054 1.84222 9.12098 1.86018 9.33112C1.87814 9.54126 1.96208 9.74034 2.1 9.89989L3.37 11.3399C3.53032 11.5224 3.61873 11.757 3.61873 11.9999C3.61873 12.2428 3.53032 12.4774 3.37 12.6599L2.1 14.0999C1.96208 14.2595 1.87814 14.4585 1.86018 14.6687C1.84222 14.8788 1.89116 15.0892 2 15.2699L4 18.7299C4.1051 18.9124 4.26512 19.057 4.45725 19.1432C4.64938 19.2294 4.86382 19.2527 5.07 19.2099L6.95 18.8299C7.18922 18.7805 7.43825 18.8203 7.65011 18.9419C7.86197 19.0635 8.022 19.2584 8.1 19.4899L8.71 21.3199C8.77958 21.5269 8.91499 21.7054 9.09556 21.8282C9.27613 21.951 9.49193 22.0113 9.71 21.9999H13.71C13.9196 22.0004 14.1242 21.9351 14.2946 21.8131C14.4651 21.691 14.5929 21.5185 14.66 21.3199L15.27 19.4899C15.348 19.2584 15.508 19.0635 15.7199 18.9419C15.9317 18.8203 16.1808 18.7805 16.42 18.8299L18.3 19.2099C18.5062 19.2527 18.7206 19.2294 18.9127 19.1432C19.1049 19.057 19.2649 18.9124 19.37 18.7299L21.37 15.2699C21.4762 15.0872 21.5217 14.8756 21.5002 14.6654C21.4786 14.4552 21.391 14.2572 21.25 14.0999L19.9 12.6599ZM18.41 13.9999L19.21 14.8999L17.93 17.1199L16.75 16.8799C16.0298 16.7327 15.2806 16.855 14.6446 17.2237C14.0086 17.5924 13.5301 18.1817 13.3 18.8799L12.92 19.9999H10.36L10 18.8599C9.76985 18.1617 9.29138 17.5724 8.6554 17.2037C8.01943 16.835 7.27022 16.7127 6.55 16.8599L5.37 17.0999L4.07 14.8899L4.87 13.9899C5.36195 13.4399 5.63393 12.7278 5.63393 11.9899C5.63393 11.252 5.36195 10.5399 4.87 9.98989L4.07 9.0899L5.35 6.88989L6.53 7.1299C7.25022 7.27712 7.99943 7.15478 8.6354 6.78609C9.27138 6.41741 9.74985 5.82805 9.98 5.1299L10.36 3.9999H12.92L13.3 5.13989C13.5301 5.83805 14.0086 6.42741 14.6446 6.79609C15.2806 7.16478 16.0298 7.28712 16.75 7.13989L17.93 6.8999L19.21 9.11989L18.41 10.0199C17.9236 10.5687 17.655 11.2766 17.655 12.0099C17.655 12.7432 17.9236 13.4511 18.41 13.9999ZM11.64 7.9999C10.8489 7.9999 10.0755 8.23449 9.41772 8.67402C8.75992 9.11354 8.24723 9.73826 7.94448 10.4692C7.64173 11.2001 7.56252 12.0043 7.71686 12.7803C7.8712 13.5562 8.25216 14.2689 8.81157 14.8283C9.37098 15.3877 10.0837 15.7687 10.8596 15.923C11.6356 16.0774 12.4398 15.9982 13.1707 15.6954C13.9016 15.3927 14.5264 14.88 14.9659 14.2222C15.4054 13.5644 15.64 12.791 15.64 11.9999C15.64 10.939 15.2186 9.92161 14.4684 9.17147C13.7183 8.42132 12.7009 7.9999 11.64 7.9999ZM11.64 13.9999C11.2444 13.9999 10.8578 13.8826 10.5289 13.6628C10.2 13.4431 9.94361 13.1307 9.79224 12.7653C9.64086 12.3998 9.60126 11.9977 9.67843 11.6097C9.7556 11.2218 9.94608 10.8654 10.2258 10.5857C10.5055 10.306 10.8619 10.1155 11.2498 10.0383C11.6378 9.96115 12.0399 10.0008 12.4054 10.1521C12.7708 10.3035 13.0832 10.5599 13.3029 10.8888C13.5227 11.2177 13.64 11.6043 13.64 11.9999C13.64 12.5303 13.4293 13.039 13.0542 13.4141C12.6791 13.7892 12.1704 13.9999 11.64 13.9999Z"
					fill="currentColor"
				/>
			</svg>
		),
		label: 'Settings',
		href: '/locations',
	},
];

const userMenu: Menu[] = [
	{
		icon: (
			<svg
				className="size-8"
				viewBox="0 0 24 25"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				id="logout-icon"
			>
				<path
					d="M4 12.5C4 12.7652 4.10536 13.0196 4.29289 13.2071C4.48043 13.3946 4.73478 13.5 5 13.5H12.59L10.29 15.79C10.1963 15.883 10.1219 15.9936 10.0711 16.1154C10.0203 16.2373 9.9942 16.368 9.9942 16.5C9.9942 16.632 10.0203 16.7627 10.0711 16.8846C10.1219 17.0064 10.1963 17.117 10.29 17.21C10.383 17.3037 10.4936 17.3781 10.6154 17.4289C10.7373 17.4797 10.868 17.5058 11 17.5058C11.132 17.5058 11.2627 17.4797 11.3846 17.4289C11.5064 17.3781 11.617 17.3037 11.71 17.21L15.71 13.21C15.801 13.1149 15.8724 13.0028 15.92 12.88C16.02 12.6365 16.02 12.3635 15.92 12.12C15.8724 11.9972 15.801 11.8851 15.71 11.79L11.71 7.79C11.6168 7.69676 11.5061 7.6228 11.3842 7.57234C11.2624 7.52188 11.1319 7.49591 11 7.49591C10.8681 7.49591 10.7376 7.52188 10.6158 7.57234C10.4939 7.6228 10.3832 7.69676 10.29 7.79C10.1968 7.88324 10.1228 7.99393 10.0723 8.11575C10.0219 8.23757 9.99591 8.36814 9.99591 8.5C9.99591 8.63186 10.0219 8.76243 10.0723 8.88425C10.1228 9.00607 10.1968 9.11676 10.29 9.21L12.59 11.5H5C4.73478 11.5 4.48043 11.6054 4.29289 11.7929C4.10536 11.9804 4 12.2348 4 12.5ZM17 2.5H7C6.20435 2.5 5.44129 2.81607 4.87868 3.37868C4.31607 3.94129 4 4.70435 4 5.5V8.5C4 8.76522 4.10536 9.01957 4.29289 9.20711C4.48043 9.39464 4.73478 9.5 5 9.5C5.26522 9.5 5.51957 9.39464 5.70711 9.20711C5.89464 9.01957 6 8.76522 6 8.5V5.5C6 5.23478 6.10536 4.98043 6.29289 4.79289C6.48043 4.60536 6.73478 4.5 7 4.5H17C17.2652 4.5 17.5196 4.60536 17.7071 4.79289C17.8946 4.98043 18 5.23478 18 5.5V19.5C18 19.7652 17.8946 20.0196 17.7071 20.2071C17.5196 20.3946 17.2652 20.5 17 20.5H7C6.73478 20.5 6.48043 20.3946 6.29289 20.2071C6.10536 20.0196 6 19.7652 6 19.5V16.5C6 16.2348 5.89464 15.9804 5.70711 15.7929C5.51957 15.6054 5.26522 15.5 5 15.5C4.73478 15.5 4.48043 15.6054 4.29289 15.7929C4.10536 15.9804 4 16.2348 4 16.5V19.5C4 20.2956 4.31607 21.0587 4.87868 21.6213C5.44129 22.1839 6.20435 22.5 7 22.5H17C17.7956 22.5 18.5587 22.1839 19.1213 21.6213C19.6839 21.0587 20 20.2956 20 19.5V5.5C20 4.70435 19.6839 3.94129 19.1213 3.37868C18.5587 2.81607 17.7956 2.5 17 2.5Z"
					fill="currentColor"
				/>
			</svg>
		),
		label: 'Logout',
		href: '/logout',
	},
];

const SidebarHeader = ({ expanded }: { expanded: boolean }) => {
	return (
		<div
			className={`flex flex-col w-full items-center justify-between ${expanded ? 'justify-between p-2' : 'justify-center pt-1 pb-5'}`}
		>
			<div>
				<NavLink
					to="/"
					end
					onClick={(e) => {
						e.preventDefault();
						window.location.assign('/');
					}}
				>
					<img
						src={expanded ? HDLogo : HDLogoCollapsed}
						alt="hydrodigital_logo"
						style={{ width: '100%', cursor: 'pointer' }}
						data-testid="hydrodigital-logo"
					/>
				</NavLink>
			</div>
			<div>
				<NavLink to="/nwl" end>
					<img
						src={expanded ? NWLLogo : NWLLogoCollapsed}
						alt="Northumbrian_Water_logo"
						style={{ width: '100%', position: 'relative', bottom: '-5px', cursor: 'pointer' }}
						data-testid="nwl-logo"
					/>
				</NavLink>
			</div>
		</div>
	);
};

const SidebarMenu = ({
	menuItems,
	expanded,
	urlOverrides,
	onMenuItemClick,
	activeMenuKey,
	setActiveMenuKey,
}: {
	menuItems: Menu[];
	expanded: boolean;
	urlOverrides?: Map<string, () => void>;
	onMenuItemClick?: () => void;
	activeMenuKey: string | null;
	setActiveMenuKey: (k: string | null) => void;
}) => {
	return (
		<ul>
			{menuItems.map((item) => {
				const callback = urlOverrides?.get(item.href);
				return (
					<MenuItem
						key={item.label}
						icon={item.icon}
						label={item.label}
						href={item.href}
						expanded={expanded}
						onClickHandler={callback}
						onBeforeNavigate={onMenuItemClick}
						activeMenuKey={activeMenuKey}
						setActiveMenuKey={setActiveMenuKey}
					/>
				);
			})}
		</ul>
	);
};

const MenuItem = ({
	icon,
	label,
	href,
	expanded,
	onClickHandler,
	onBeforeNavigate,
	activeMenuKey,
	setActiveMenuKey,
}: {
	icon: React.ReactNode;
	label: string;
	href: string;
	expanded: boolean;
	onClickHandler?: () => void;
	onBeforeNavigate?: () => void;
	activeMenuKey: string | null;
	setActiveMenuKey: (k: string | null) => void;
}) => {
	const isSelected = activeMenuKey === href;
	return (
		<NavLink
			to={href}
			end
			onClick={(e) => {
				onBeforeNavigate?.();
				if (!onClickHandler) setActiveMenuKey(href);
				if (onClickHandler) {
					e.preventDefault();
					onClickHandler();
				}
			}}
		>
			<li
				className={`sidebar-item ${expanded ? 'px-4' : 'px-[12px] sidebar-item-circle'} ${
					isSelected && !onClickHandler ? 'selected-sidebar-item' : ''
				}`}
				data-testid={testIdFormatter(label, 'sidebar-menu-item')}
			>
				<span className="menu-item-icon">{icon}</span>
				{expanded && <span className="ml-2">{label}</span>}
			</li>
		</NavLink>
	);
};

type StoredLocation = {
	locationId: number | string;
	name?: string;
	label?: string;
	locationName?: string;
	id?: number | string;
};

const NETWORK_ROUTE = '/nwl/catchment';
const NETWORK_MENU_REOPEN_KEY = 'nwl:networkMenu:reopen';
const NETWORK_KEY = 'network';

const NetworkIcon = (
	<svg width="33" height="33" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M22 20C22 18.8954 21.1046 18 20 18C18.8954 18 18 18.8954 18 20C18 21.1046 18.8954 22 20 22C21.1046 22 22 21.1046 22 20ZM6 19C6 18.4477 5.55228 18 5 18C4.44772 18 4 18.4477 4 19C4 19.5523 4.44772 20 5 20C5.55228 20 6 19.5523 6 19ZM16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12ZM20 5C20 4.44772 19.5523 4 19 4C18.4477 4 18 4.44772 18 5C18 5.55228 18.4477 6 19 6C19.5523 6 20 5.55228 20 5ZM6 4C6 2.89543 5.10457 2 4 2C2.89543 2 2 2.89543 2 4C2 5.10457 2.89543 6 4 6C5.10457 6 6 5.10457 6 4ZM8 4C8 4.74175 7.79715 5.43577 7.44531 6.03125L8.52441 7.11035C9.50521 6.41194 10.7042 6 12 6C13.2955 6 14.4939 6.41222 15.4746 7.11035L16.2949 6.29102C16.1078 5.89962 16 5.46277 16 5C16 3.34315 17.3431 2 19 2C20.6569 2 22 3.34315 22 5C22 6.65685 20.6569 8 19 8C18.5368 8 18.0997 7.89158 17.708 7.7041L17.707 7.70703L16.8887 8.52441C17.5873 9.5053 18 10.704 18 12C18 13.2957 17.587 14.4938 16.8887 15.4746L17.9678 16.5537C18.5633 16.2018 19.2581 16 20 16C22.2091 16 24 17.7909 24 20C24 22.2091 22.2091 24 20 24C17.7909 24 16 22.2091 16 20C16 19.2581 16.2018 18.5633 16.5537 17.9678L15.4746 16.8887C14.4938 17.587 13.2957 18 12 18C10.704 18 9.5053 17.5873 8.52441 16.8887L7.70703 17.707L7.7041 17.708C7.89158 18.0997 8 18.5368 8 19C8 20.6569 6.65685 22 5 22C3.34315 22 2 20.6569 2 19C2 17.3431 3.34315 16 5 16C5.46277 16 5.89962 16.1078 6.29102 16.2949L7.11035 15.4746C6.41222 14.4939 6 13.2955 6 12C6 10.7042 6.41194 9.50521 7.11035 8.52441L6.03125 7.44531C5.43577 7.79715 4.74175 8 4 8C1.79086 8 0 6.20914 0 4C0 1.79086 1.79086 0 4 0C6.20914 0 8 1.79086 8 4Z"
			fill="currentColor"
		/>
	</svg>
);

const readAvailableLocations = (): StoredLocation[] => {
	try {
		const raw = JSON.parse(localStorage.getItem('locations') ?? '[]');
		if (!Array.isArray(raw)) return [];

		const normalised = raw
			.map((l: any) => {
				const locationId = l?.locationId ?? l?.id;
				if (locationId == null) return null;

				return {
					locationId,
					name: l?.name,
					label: l?.label,
					locationName: l?.locationName,
				} as StoredLocation;
			})
			.filter(Boolean) as StoredLocation[];

		normalised.sort((a, b) => {
			const an = (a.label ?? a.locationName ?? a.name ?? `Location ${a.locationId}`).toString();
			const bn = (b.label ?? b.locationName ?? b.name ?? `Location ${b.locationId}`).toString();
			return an.localeCompare(bn);
		});

		return normalised;
	} catch {
		return [];
	}
};

const readSelectedLocation = (): StoredLocation | null => {
	const raw = localStorage.getItem('location');
	if (!raw) return null;

	try {
		const parsed = JSON.parse(raw);

		if (parsed && typeof parsed === 'object') {
			const locationId = parsed.locationId ?? parsed.id;
			if (locationId == null) return null;

			return {
				locationId,
				name: parsed.name,
				label: parsed.label,
				locationName: parsed.locationName,
			};
		}

		if (typeof parsed === 'string' || typeof parsed === 'number') {
			return { locationId: parsed };
		}
	} catch {
		return { locationId: raw };
	}

	return null;
};

const writeSelectedLocation = (loc: StoredLocation | null) => {
	if (!loc) {
		localStorage.removeItem('location');
	} else {
		localStorage.setItem(
			'location',
			JSON.stringify({
				locationId: loc.locationId,
				name: loc.name,
				label: loc.label,
				locationName: loc.locationName,
			}),
		);
	}

	window.dispatchEvent(new Event('nwl:locations-updated'));
};

const NetworkLocationsMenu = ({
	expanded,
	pinnedSelected,
	setPinnedSelected,
	activeMenuKey,
	setActiveMenuKey,
}: {
	expanded: boolean;
	pinnedSelected: boolean;
	setPinnedSelected: (next: boolean) => void;
	activeMenuKey: string | null;
	setActiveMenuKey: (k: string | null) => void;
}) => {
	const navigate = useNavigate();
	const route = useLocation();

	const [open, setOpen] = useState(false);
	const [locations, setLocations] = useState<StoredLocation[]>([]);
	const [selected, setSelected] = useState<StoredLocation | null>(null);

	const anchorRef = useRef<HTMLButtonElement | null>(null);
	const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});

	const isNetworkRoute = route.pathname.startsWith(NETWORK_ROUTE);
	const headerIsSelected = activeMenuKey === NETWORK_KEY;

	const prevExpandedRef = useRef(expanded);

	useEffect(() => {
		if (activeMenuKey !== NETWORK_KEY) setOpen(false);
	}, [activeMenuKey]);

	useEffect(() => {
		if (prevExpandedRef.current && !expanded) setOpen(false);
		prevExpandedRef.current = expanded;
	}, [expanded]);

	useEffect(() => {
		const shouldReopen = localStorage.getItem(NETWORK_MENU_REOPEN_KEY) === '1';
		if (!shouldReopen) return;

		localStorage.removeItem(NETWORK_MENU_REOPEN_KEY);
		setPinnedSelected(true);
		setOpen(true);
	}, [setPinnedSelected]);

	const onHeaderClick = () => {
		setPinnedSelected(true);
		setActiveMenuKey(NETWORK_KEY);
		setOpen((v) => !v);
	};

	useEffect(() => {
		const sync = () => {
			setLocations(readAvailableLocations());
			setSelected(readSelectedLocation());
		};

		sync();
		window.addEventListener('nwl:locations-updated', sync);
		window.addEventListener('storage', sync);

		return () => {
			window.removeEventListener('nwl:locations-updated', sync);
			window.removeEventListener('storage', sync);
		};
	}, []);

	useEffect(() => {
		if (!expanded) setOpen(false);
	}, [expanded]);

	useEffect(() => {
		if (!open || expanded) return;

		const r = anchorRef.current?.getBoundingClientRect();
		if (!r) return;

		setPopupStyle({
			position: 'fixed',
			left: r.right + 12,
			top: r.top,
		});
	}, [open, expanded]);

	const displayName = (loc: StoredLocation) =>
		(loc.label ?? loc.locationName ?? loc.name ?? `Location ${loc.locationId}`).toString();

	const pickOne = (loc: StoredLocation) => {
		setPinnedSelected(true);

		writeSelectedLocation(loc);
		setActiveLocationId(loc.locationId);

		setSelected(loc);
		setActiveMenuKey(NETWORK_KEY);

		navigate(NETWORK_ROUTE);
		localStorage.setItem(NETWORK_MENU_REOPEN_KEY, '1');
		setOpen(true);
		setTimeout(() => navigate(0), 0);
	};

	const HeaderButton = (
		<button
			type="button"
			onClick={onHeaderClick}
			aria-expanded={open}
			className={[
				'sidebar-item',
				expanded ? 'px-4' : 'px-[10px] sidebar-item-circle',
				'btn-countdown',
				headerIsSelected ? 'is-selected' : '',
				open || isNetworkRoute ? 'is-open' : '',
			].join(' ')}
			data-testid={testIdFormatter('Network', 'sidebar-menu-item')}
		>
			<span className="menu-item-icon">{NetworkIcon}</span>
			{expanded && <span className="ml-1">Network</span>}
			{expanded && (
				<span className="ml-auto" aria-hidden="true">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth="3"
						stroke="currentColor"
						className="size-4"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d={open ? 'm18 15-6-6-6 6' : 'm6 9 6 6 6-6'}
						/>
					</svg>
				</span>
			)}
		</button>
	);

	const Panel = (
		<div className="network-menu__panel">
			<div className="network-menu__list" role="list">
				<div className="network-menu__line" aria-hidden="true" />

				{locations.map((loc) => {
					const name = displayName(loc);
					const isSelected =
						headerIsSelected && String(selected?.locationId) === String(loc?.locationId);

					return (
						<button
							key={String(loc.locationId)}
							type="button"
							onClick={() => pickOne(loc)}
							className={[
								'mb-1 network-menu__loc btn-countdown',
								isSelected ? 'is-selected' : '',
							].join(' ')}
							data-testid={testIdFormatter(name, 'sidebar-menu-item')}
						>
							<span className="network-menu__dot" aria-hidden="true" />
							<span className="ps-2">{name}</span>
						</button>
					);
				})}
			</div>
		</div>
	);

	if (!expanded) {
		return (
			<div>
				{HeaderButton}
				{open && (
					<div
						className="z-[1000]"
						style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
						onClick={() => setOpen(false)}
					>
						<div
							className="network-popup-menu p-1"
							style={{ position: 'fixed', left: 10, top: 270 }}
							onClick={(e) => e.stopPropagation()}
						>
							{Panel}
						</div>
					</div>
				)}
			</div>
		);
	}

	return (
		<div>
			{HeaderButton}
			{open && Panel}
		</div>
	);
};

type RefreshIndicatorProps = {
	expanded: boolean;
	nextUpdateInSec?: number;
	onClick?: (lastSuccessful?: Date) => void | Promise<void>;
	onElapsed?: (ranAt?: Date) => void | Promise<void>;
	selected: boolean;
	onSelectedChange: (next: boolean) => void;
};

const RefreshIndicator = ({
	expanded,
	nextUpdateInSec = 15 * 60,
	onClick,
	onElapsed,
	selected,
	onSelectedChange,
}: RefreshIndicatorProps) => {
	const activeLocationId = useActiveLocationId();

	const [remaining, setRemaining] = useState<number | null>(null);
	const [targetEpochSec, setTargetEpochSec] = useState<number | null>(null);
	const [bootstrapped, setBootstrapped] = useState(false);
	const lastSuccessAtRef = useRef<Date | null>(null);
	const zeroRunGuardRef = useRef(false);
	const lastLocRef = useRef<number | string | null>(null);
	const digitalTwinIdRef = useRef<number | null>(null);

	const setEpoch = useCallback((epoch: number, persist: boolean) => {
		if (persist) {
			localStorage.setItem('nwl:nextSimulationEpoch', String(epoch));
		}
		setTargetEpochSec(epoch);

		const now = Math.floor(Date.now() / 1000);
		setRemaining(Math.max(0, epoch - now));
	}, []);

	const extractPayloadFromSimResp = useCallback((simResp: any) => {
		if (!simResp) return null;
		if (Array.isArray(simResp)) return simResp.find((p) => Array.isArray(p?.data)) ?? null;
		if (Array.isArray(simResp?.data)) return simResp;
		return null;
	}, []);

	const readOrgId = () => {
		try {
			return JSON.parse(localStorage.getItem('security') ?? '{}')?.organizationId;
		} catch {
			return undefined;
		}
	};

	const performSimulation = useCallback(async (): Promise<boolean> => {
		const now = Math.floor(Date.now() / 1000);
		const fallback = now + nextUpdateInSec;

		const organizationId = readOrgId();
		const locId = activeLocationId;

		if (!organizationId || !locId) {
			setTargetEpochSec(null);
			setRemaining(null);
			return false;
		}

		try {
			let dtId = digitalTwinIdRef.current;

			if (!dtId || String(lastLocRef.current) !== String(locId)) {
				const resp = await getDigitalTwins(organizationId, locId);
				dtId = resp?.id ?? null;
				digitalTwinIdRef.current = dtId;
				lastLocRef.current = locId;
			}

			if (!dtId) {
				setEpoch(fallback, false);
				return false;
			}

			const nextResults = await getDigitalTwinsNextSimulation(organizationId, locId, dtId);
			const epochs = (Array.isArray(nextResults) ? nextResults : [nextResults])
				.map((r) => Number(r?.nextSimulation))
				.filter((n) => Number.isFinite(n) && n > 0);

			if (epochs.length) {
				setEpoch(Math.min(...epochs), true);
				return true;
			}

			setEpoch(fallback, false);
			return false;
		} catch {
			setEpoch(fallback, false);
			return false;
		}
	}, [activeLocationId, nextUpdateInSec, setEpoch]);

	useEffect(() => {
		const read = () => {
			const iso = localStorage.getItem('nwl:lastSuccessAtISO');
			if (!iso) return;
			const d = new Date(iso);
			if (!Number.isNaN(+d)) lastSuccessAtRef.current = d;
		};

		read();
		const onBroadcast = () => read();
		window.addEventListener('nwl:last-success-updated', onBroadcast);
		return () => window.removeEventListener('nwl:last-success-updated', onBroadcast);
	}, []);

	useEffect(() => {
		const stored = Number(localStorage.getItem('nwl:nextSimulationEpoch'));
		if (Number.isFinite(stored) && stored > 0) {
			setEpoch(stored, false);
		}
		setBootstrapped(true);
		void performSimulation();
	}, [performSimulation, setEpoch]);

	useEffect(() => {
		zeroRunGuardRef.current = false;
		digitalTwinIdRef.current = null;
		lastLocRef.current = null;
		void performSimulation();
	}, [activeLocationId]);

	useEffect(() => {
		const sync = () => {
			const stored = Number(localStorage.getItem('nwl:nextSimulationEpoch'));
			if (Number.isFinite(stored) && stored > 0) {
				const now = Math.floor(Date.now() / 1000);
				setTargetEpochSec(stored);
				setRemaining(Math.max(0, stored - now));
			}
		};

		const onUpdated = () => sync();
		window.addEventListener('nwl:simulation-updated', onUpdated);

		const onStorage = (e: StorageEvent) => {
			if (e.key === 'nwl:nextSimulationEpoch') sync();
		};
		window.addEventListener('storage', onStorage);

		return () => {
			window.removeEventListener('nwl:simulation-updated', onUpdated);
			window.removeEventListener('storage', onStorage);
		};
	}, []);

	useEffect(() => {
		if (!targetEpochSec) return;
		const id = setInterval(() => {
			const now = Math.floor(Date.now() / 1000);
			setRemaining(Math.max(0, targetEpochSec - now));
		}, 1000);
		return () => clearInterval(id);
	}, [targetEpochSec]);

	useEffect(() => {
		if (remaining !== 0 || zeroRunGuardRef.current) return;
		zeroRunGuardRef.current = true;

		const run = async () => {
			const organizationId = readOrgId();
			const locId = activeLocationId;

			try {
				if (!organizationId || !locId) return;

				let dtId = digitalTwinIdRef.current;
				if (!dtId || String(lastLocRef.current) !== String(locId)) {
					const resp = await getDigitalTwins(organizationId, locId);
					dtId = resp?.id ?? null;
					digitalTwinIdRef.current = dtId;
					lastLocRef.current = locId;
				}
				if (!dtId) return;

				const simResp = await getDigitalTwinsSimulations(organizationId, locId, dtId);
				const payload = extractPayloadFromSimResp(simResp);
				const successAt = lockFirstSuccessCompletedSimTime(payload);

				if (successAt) {
					lastSuccessAtRef.current = successAt;
					await onElapsed?.(successAt);
					window.dispatchEvent(new Event('nwl:simulation-updated'));
				} else {
					await onElapsed?.(undefined);
					window.dispatchEvent(new Event('nwl:simulation-updated'));
				}
			} finally {
				await performSimulation();
				zeroRunGuardRef.current = false;
			}
		};

		void run();
	}, [remaining, activeLocationId, extractPayloadFromSimResp, performSimulation, onElapsed]);

	// Labels
	const minutes = remaining == null ? null : Math.floor(remaining / 60);
	const seconds = remaining == null ? null : remaining % 60;

	const labelDesc = useMemo(() => {
		if (remaining == null) return '';
		if (remaining <= 0) return 'Data update starting';
		const minPart = minutes ? `${minutes} minute${minutes === 1 ? '' : 's'}` : '';
		const secPart = `${seconds} second${seconds === 1 ? '' : 's'}`;
		return `Data update in ${minPart}${minutes ? ' and ' : ''}${secPart}`;
	}, [minutes, seconds, remaining]);

	const timeLabel = useMemo(() => {
		if (remaining == null) return '';
		const mm = String(minutes).padStart(2, '0');
		const ss = String(seconds).padStart(2, '0');
		return `${mm}:${ss} mins`;
	}, [minutes, seconds, remaining]);

	const handleClick = async () => {
		onSelectedChange(!selected);
		await onClick?.(lastSuccessAtRef.current ?? undefined);
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			aria-pressed={selected}
			aria-label={labelDesc || 'Data update'}
			className={[
				'text-muted',
				'btn-countdown',
				expanded ? 'btn-countdown--expanded' : 'btn-countdown--collapsed',
				selected ? 'is-selected' : '',
				!bootstrapped || remaining == null ? 'is-bootstrapping' : '',
			].join(' ')}
			id="countdown-refresh-indicator-button"
		>
			{/* announce countdown changes for SR users */}
			<span className="sr-only" aria-live={remaining == null ? 'off' : 'polite'}>
				{labelDesc}
			</span>

			<span className="btn-countdown__icon" aria-hidden="true">
				<svg
					width="24"
					height="25"
					viewBox="0 0 24 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M3 19.5652V15.0652H9V13.5652H3V9.06525H16.5V3.06525C16.5 2.66742 16.342 2.28589 16.0607 2.00459C15.7794 1.72328 15.3978 1.56525 15 1.56525H3C2.60218 1.56525 2.22064 1.72328 1.93934 2.00459C1.65804 2.28589 1.5 2.66742 1.5 3.06525V19.5652C1.5 19.9631 1.65804 20.3446 1.93934 20.6259C2.22064 20.9072 2.60218 21.0652 3 21.0652H9V19.5652H3ZM3 3.06525H15V7.56525H3V3.06525ZM5.25 6.06525C5.66421 6.06525 6 5.72946 6 5.31525C6 4.90103 5.66421 4.56525 5.25 4.56525C4.83579 4.56525 4.5 4.90103 4.5 5.31525C4.5 5.72946 4.83579 6.06525 5.25 6.06525ZM5.25 12.0652C5.66421 12.0652 6 11.7295 6 11.3152C6 10.901 5.66421 10.5652 5.25 10.5652C4.83579 10.5652 4.5 10.901 4.5 11.3152C4.5 11.7295 4.83579 12.0652 5.25 12.0652ZM6 17.3152C6 17.7295 5.66421 18.0652 5.25 18.0652C4.83579 18.0652 4.5 17.7295 4.5 17.3152C4.5 16.901 4.83579 16.5652 5.25 16.5652C5.66421 16.5652 6 16.901 6 17.3152ZM21.0001 14.6251V12.8153H22.5001V17.3153H18.0001V15.8153H19.9322C19.6617 15.1955 19.2286 14.6603 18.6788 14.2665C18.129 13.8728 17.4829 13.635 16.809 13.5784C16.1351 13.5219 15.4584 13.6486 14.8506 13.9452C14.2428 14.2418 13.7266 14.6973 13.3565 15.2633C12.9865 15.8294 12.7764 16.485 12.7485 17.1607C12.7206 17.8364 12.876 18.5071 13.1982 19.1017C13.5203 19.6963 13.9973 20.1928 14.5786 20.5384C15.1599 20.8841 15.8238 21.0661 16.5001 21.0653V22.5653C15.5823 22.5658 14.6805 22.3256 13.8846 21.8687C13.0887 21.4118 12.4265 20.7541 11.9643 19.9613C11.502 19.1685 11.2557 18.2683 11.2501 17.3505C11.2445 16.4328 11.4796 15.5296 11.9321 14.7312C12.3846 13.9328 13.0386 13.267 13.8289 12.8003C14.6191 12.3336 15.5179 12.0824 16.4356 12.0716C17.3533 12.0609 18.2577 12.2911 19.0587 12.7391C19.8596 13.1872 20.529 13.8375 21.0001 14.6251Z"
						fill="currentColor"
					/>
				</svg>
			</span>

			{expanded ? (
				<span className="btn-countdown__text">
					<span className="btn-countdown__title" id="refresh-indicator-label">
						Data update
					</span>
					<span className="btn-countdown__time" id="refresh-indicator-time-expanded">
						{timeLabel}
					</span>
				</span>
			) : (
				<span className="btn-countdown__text-collapsed" id="refresh-indicator-time-collapsed">
					{timeLabel}
				</span>
			)}
		</button>
	);
};

const CollapseButton = ({
	expanded,
	setExpanded,
}: {
	expanded: boolean;
	setExpanded: (expanded: boolean) => void;
}) => {
	return (
		<div style={{ position: 'absolute', top: '16px', right: '-12px', zIndex: 100 }}>
			<button
				onClick={() => setExpanded(!expanded)}
				className="collapse-btn"
				data-testid="sidebar-toggle-btn"
			>
				{expanded ? (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth="1.5"
						stroke="currentColor"
						className="size-4"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
					</svg>
				) : (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth="1.5"
						stroke="currentColor"
						className="size-4"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
					</svg>
				)}
			</button>
		</div>
	);
};

const UserCard = ({
	expanded,
	initials,
	name,
	className,
}: {
	expanded: boolean;
	initials: string;
	name: string;
	className?: string;
}) => {
	return (
		<div className={`user-card ${className}`}>
			<div className="avatar">{initials}</div>
			{expanded && <div className="ml-2 user-text">{name}</div>}
		</div>
	);
};

const UserProfile = ({
	expanded,
	urlOverrides,
	activeMenuKey,
	setActiveMenuKey,
}: {
	expanded: boolean;
	urlOverrides?: Map<string, () => void>;
	activeMenuKey: string | null;
	setActiveMenuKey: (k: string | null) => void;
}) => {
	const security = JSON.parse(localStorage.getItem('security') ?? '{}');
	const name = security?.firstname;
	const fullname = `${security?.firstname} ${security?.lastname}`;
	const initials = name?.charAt(0).toUpperCase() + security?.lastname?.charAt(0).toUpperCase();
	const [userMenuOpened, setUserMenuOpened] = useState(false);

	return (
		<div>
			<div
				className={`user-profile ${expanded ? 'justify-between' : 'justify-center sidebar-item-circle'} ${userMenuOpened ? 'bg-[var(--sidebar-item-selected-color)] hover:bg-[var(--sidebar-item-selected-color)]' : ''}`}
				onClick={() => setUserMenuOpened(!userMenuOpened)}
				data-testid="user-profile"
			>
				<UserCard
					expanded={expanded}
					initials={initials}
					name={name}
					className={userMenuOpened ? 'user-card-selected' : ''}
				/>
				{expanded && (
					<div
						className={`ml-2 ${userMenuOpened ? 'text-white' : 'text-[var(--sidebar-item-selected-color)]'}`}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="3"
							stroke="currentColor"
							className="size-4"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
						</svg>
					</div>
				)}
			</div>
			{userMenuOpened && (
				<div
					className="z-[1000]"
					style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
					onClick={() => setUserMenuOpened(false)}
				>
					<div
						className={`user-popup-menu p-2 ${expanded ? 'left-[270px]' : 'left-[100px]'}`}
						data-testid="user-popup-menu"
					>
						<div className="mb-2 cursor-pointer" onClick={() => setUserMenuOpened(false)}>
							<UserCard expanded={true} initials={initials} name={fullname} />
						</div>
						<div className="border-t border-gray-200 pt-1">
							<SidebarMenu
								menuItems={userMenu}
								expanded={true}
								urlOverrides={urlOverrides}
								activeMenuKey={activeMenuKey}
								setActiveMenuKey={setActiveMenuKey}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const Sidebar = ({
	leadingMenu = menu,
	trailingMenu = settingsMenu,
}: {
	leadingMenu?: Menu[];
	trailingMenu?: Menu[];
}) => {
	const {
		decisionEngine: { sidebarExpanded, setSidebarExpanded },
	} = useContext<any>(NWLContext);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const [showCountdownInfo, setShowCountdownInfo] = useState(false);
	const [lastSuccessfulRefreshDate, setLastSuccessfulRefreshDate] = useState<Date>();
	const [refreshSelected, setRefreshSelected] = useState(false);
	const [networkPinnedSelected, setNetworkPinnedSelected] = useState(false);
	const clearNetworkPinned = () => setNetworkPinnedSelected(false);
	const urlOverrides = new Map<string, () => void>();
	urlOverrides.set('/logout', () => setShowLogoutModal(true));

	const keyFromPath = (pathname: string) => {
		if (pathname.startsWith(NETWORK_ROUTE)) return NETWORK_KEY;
		if (pathname === '/nwl') return '/nwl';
		if (pathname.startsWith('/nwl/decision-log')) return '/nwl/decision-log';
		if (pathname.startsWith('/locations')) return '/locations';
		return null;
	};

	const route = useLocation();
	const [activeMenuKey, setActiveMenuKey] = useState<string | null>(() =>
		keyFromPath(route.pathname),
	);

	useEffect(() => {
		setActiveMenuKey(keyFromPath(route.pathname));
	}, [route.pathname]);

	return (
		<div className={`sidebar ${sidebarExpanded ? 'w-[256px]' : 'w-[96px]'} `}>
			<CollapseButton expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />
			<div>
				<div className="px-5 mb-5">
					<SidebarHeader expanded={sidebarExpanded} />
				</div>
				<div className="px-5">
					<div className="mb-4">
						{(() => {
							const dashboard = leadingMenu.find((i) => i.href === '/nwl') ?? leadingMenu[0];
							const rest = leadingMenu.filter((i) => i !== dashboard);

							return (
								<ul>
									{dashboard && (
										<MenuItem
											key={dashboard.label}
											icon={dashboard.icon}
											label={dashboard.label}
											href={dashboard.href}
											expanded={sidebarExpanded}
											onBeforeNavigate={clearNetworkPinned}
											activeMenuKey={activeMenuKey}
											setActiveMenuKey={setActiveMenuKey}
										/>
									)}

									<li
										className={sidebarExpanded ? 'px-0' : 'px-0'}
										style={{ marginTop: 8, marginBottom: 8 }}
									>
										<NetworkLocationsMenu
											expanded={sidebarExpanded}
											pinnedSelected={networkPinnedSelected}
											setPinnedSelected={setNetworkPinnedSelected}
											activeMenuKey={activeMenuKey}
											setActiveMenuKey={setActiveMenuKey}
										/>
									</li>

									{rest.map((item) => (
										<MenuItem
											key={item.label}
											icon={item.icon}
											label={item.label}
											href={item.href}
											expanded={sidebarExpanded}
											onBeforeNavigate={clearNetworkPinned}
											activeMenuKey={activeMenuKey}
											setActiveMenuKey={setActiveMenuKey}
										/>
									))}
								</ul>
							);
						})()}
					</div>
					<div className="border-t border-gray-200 py-3">
						<SidebarMenu
							menuItems={trailingMenu}
							expanded={sidebarExpanded}
							onMenuItemClick={clearNetworkPinned}
							activeMenuKey={activeMenuKey}
							setActiveMenuKey={setActiveMenuKey}
						/>
					</div>
				</div>
			</div>
			<div>
				<div className="px-2">
					<div className="border-t border-gray-200 py-5">
						<RefreshIndicator
							expanded={sidebarExpanded}
							selected={refreshSelected}
							onSelectedChange={setRefreshSelected}
							onClick={(timestamp) => {
								setLastSuccessfulRefreshDate(timestamp);
								setShowCountdownInfo(true);
							}}
							onElapsed={(timestamp) => {
								setLastSuccessfulRefreshDate(timestamp);
							}}
						/>
					</div>
				</div>
				<div className="border-t border-gray-200 py-3 px-2">
					<UserProfile
						expanded={sidebarExpanded}
						urlOverrides={urlOverrides}
						activeMenuKey={activeMenuKey}
						setActiveMenuKey={setActiveMenuKey}
					/>
				</div>
			</div>
			{showLogoutModal && <ModalExit closeModal={setShowLogoutModal} />}
			{showCountdownInfo && (
				<CountdownInfoModal
					open={showCountdownInfo}
					onClose={() => {
						setShowCountdownInfo(false);
						setRefreshSelected(false);
					}}
					lastSuccessAt={lastSuccessfulRefreshDate}
				/>
			)}
		</div>
	);
};

export default Sidebar;
